import { breakdownRowName, cooperateDisplayName, driverDisplayName, millisToHours, timelineStatusLabel } from './kpi-helpers';
import { CHART_COLORS, THEME_COLORS } from './chart-theme';

export function topItems(items: any[], limit = 8): any[] {
  return [...(items || [])]
    .sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0))
    .slice(0, limit);
}

/**
 * Share of total as a display percent.
 * >= 1% → whole number (78); &lt; 1% → up to 4 decimals (0.23, 0.023, 0.0023) so tiny slices are not shown as 0.
 */
export function formatSharePercent(value: number, total: number): string {
  if (!total) return '0';
  const pct = ((Number(value) || 0) / total) * 100;
  if (!pct) return '0';
  if (pct >= 1) return String(Math.round(pct));
  return String(parseFloat(pct.toFixed(4)));
}

export function mapCooperateRows(rows: any[], valueKey = 'totalJobs'): any[] {
  return (rows || []).map((row) => ({
    label: cooperateDisplayName(row),
    value: Number(row[valueKey]) || 0
  }));
}

export function mapDriverRows(rows: any[], valueKey = 'totalJobs'): any[] {
  return (rows || []).map((row) => ({
    label: driverDisplayName(row.driver),
    value: Number(row[valueKey]) || 0
  }));
}

export function mapBreakdownRows(rows: any[], nameField: string, valueKey = 'totalJobs'): any[] {
  return (rows || []).map((row) => ({
    label: breakdownRowName(row, nameField),
    value: Number(row[valueKey]) || 0
  }));
}

export function tripStatusSegments(total: any, completed: any, cancelled: any): any[] {
  const totalNum = Number(total) || 0;
  const completedNum = Number(completed) || 0;
  const cancelledNum = Number(cancelled) || 0;
  const other = Math.max(0, totalNum - completedNum - cancelledNum);

  return [
    { label: 'Completed', value: completedNum, color: THEME_COLORS.success },
    { label: 'Cancelled', value: cancelledNum, color: THEME_COLORS.danger },
    { label: 'Other', value: other, color: THEME_COLORS.info }
  ].filter((item) => item.value > 0);
}

export function cancellationBreakdownSegments(breakdown: any = {}): any[] {
  return [
    { label: 'CAD / No Show (incl. Dry Run)', value: Number(breakdown.cadNoShow) || 0, color: THEME_COLORS.warning },
    { label: 'Vendor Returned', value: Number(breakdown.vendorReturned) || 0, color: THEME_COLORS.info },
    { label: 'Other Cancelled', value: Number(breakdown.otherCancelled) || 0, color: THEME_COLORS.danger }
  ].filter((item) => item.value > 0);
}

export function revenueStatusSegments(cards: any): any[] {
  return [
    { label: 'Completed', value: Number(cards.totalCompletedJobs) || 0, color: THEME_COLORS.success },
    { label: 'Cancelled', value: Number(cards.totalCancelledJobs) || 0, color: THEME_COLORS.danger },
    { label: 'CAD', value: Number(cards.totalCadJobs) || 0, color: THEME_COLORS.warning },
    { label: 'Remaining', value: Number(cards.totalRemainingJobs) || 0, color: THEME_COLORS.info }
  ].filter((item) => item.value > 0);
}

/** Donut center total — sum of displayed segments. */
export function revenueBreakdownTotal(cards: any): number {
  return revenueStatusSegments(cards).reduce((sum, item) => sum + (Number(item.value) || 0), 0);
}

export function driverDigestSegments(summary: any): any[] {
  return [
    { label: 'Checked In', value: Number(summary.checkedIn) || 0, color: THEME_COLORS.success },
    { label: 'No Timesheet', value: Number(summary.noTimesheet) || 0, color: THEME_COLORS.warning },
    { label: 'Off / Idle', value: Number(summary.offIdle) || 0, color: THEME_COLORS.danger }
  ].filter((item) => item.value > 0);
}

export function timelinessSegments(drivers: any[]): any[] {
  const totals = (drivers || []).reduce(
    (acc, row) => ({
      onTime: acc.onTime + (Number(row.onTimeTrips) || 0),
      early: acc.early + (Number(row.earlyTrips) || 0),
      late: acc.late + (Number(row.lateTrips) || 0)
    }),
    { onTime: 0, early: 0, late: 0 }
  );

  return [
    { label: 'On Time', value: totals.onTime, color: THEME_COLORS.success },
    { label: 'Early', value: totals.early, color: THEME_COLORS.info },
    { label: 'Late', value: totals.late, color: THEME_COLORS.warning }
  ].filter((item) => item.value > 0);
}

export function mapCooperateStackedRows(rows: any[]): any[] {
  return (rows || []).map((row) => ({
    label: cooperateDisplayName(row),
    segments: [
      { label: 'Completed', value: Number(row.totalCompletedJobs) || 0, colorClass: 'success' },
      { label: 'Cancelled', value: Number(row.totalCancelledJobs) || 0, colorClass: 'danger' },
      { label: 'CAD', value: Number(row.totalCadJobs) || 0, colorClass: 'warning' }
    ]
  }));
}

export function mapDriverMilesStackedRows(rows: any[]): any[] {
  return (rows || []).map((row) => {
    const segments = [
      { label: 'Completed', value: roundMiles(row.completedMiles), colorClass: 'success' },
      { label: 'Cancelled', value: roundMiles(row.cancelledMiles), colorClass: 'danger' },
      { label: 'CAD', value: roundMiles(row.cadMiles), colorClass: 'warning' },
      { label: 'RE/Off', value: roundMiles(row.remainingMiles), colorClass: 'info' }
    ];
    return {
      label: driverDisplayName(row.driver),
      value: segments.reduce((sum, segment) => sum + segment.value, 0),
      segments
    };
  });
}

function roundMiles(value: any): number {
  return Math.round((Number(value) || 0) * 10) / 10;
}

export function mapDriverTimelinessStackedRows(drivers: any[]): any[] {
  return (drivers || []).map((row) => ({
    label: driverDisplayName(row.driver),
    segments: [
      { label: 'On Time', value: Number(row.onTimeTrips) || 0, colorClass: 'success' },
      { label: 'Early', value: Number(row.earlyTrips) || 0, colorClass: 'info' },
      { label: 'Late', value: Number(row.lateTrips) || 0, colorClass: 'warning' }
    ]
  }));
}

export function aggregateTimelineStatus(timelines: any[]): any[] {
  const counts: any = {};
  (timelines || []).forEach((row) => {
    const label = timelineStatusLabel(row.status);
    counts[label] = (counts[label] || 0) + 1;
  });
  return Object.entries(counts).map(([label, value]) => ({ label, value }));
}

export function topDriversByHours(timelines: any[], limit = 8): any[] {
  const totals: any = {};
  (timelines || []).forEach((row) => {
    const label = driverDisplayName(row.driver);
    const fallbackHours = millisToHours(row.workTimeInMillis);
    const hours = Number(row.todayHours != null ? row.todayHours : (fallbackHours != null ? fallbackHours : 0));
    totals[label] = (totals[label] || 0) + hours;
  });

  return topItems(
    Object.entries(totals).map(([label, value]: [string, number]) => ({
      label,
      value: Number(value.toFixed(1))
    })),
    limit
  );
}

export function tripCompletionSegments(totalTrips: any, completedTrips: any): any[] {
  const total = Number(totalTrips) || 0;
  const completed = Number(completedTrips) || 0;

  return [
    { label: 'Completed', value: completed, color: THEME_COLORS.success },
    { label: 'Not Completed', value: Math.max(0, total - completed), color: THEME_COLORS.info }
  ].filter((item) => item.value > 0);
}

export function overviewStatItems(kpiCards: any): any[] {
  return [
    { label: 'Total Trips', value: Number(kpiCards.totalTrips) || 0 },
    { label: 'Completed Trips', value: Number(kpiCards.completedTrips) || 0 },
    { label: 'On-Time Rate (%)', value: Number(kpiCards.onTimeRate) || 0 }
  ];
}

export function tripsStatItems(total: any, cancelled: any, completed: any): any[] {
  return [
    { label: 'Total Trips', value: Number(total) || 0 },
    { label: 'Completed', value: Number(completed) || 0 },
    { label: 'Cancelled', value: Number(cancelled) || 0 },
    { label: 'Other', value: Math.max(0, (Number(total) || 0) - (Number(completed) || 0) - (Number(cancelled) || 0)) }
  ];
}

export function revenueStatItems(cards: any): any[] {
  return [
    { label: 'Total Revenue', value: Number(cards.total) || 0 },
    { label: 'Completed', value: Number(cards.totalCompletedJobs) || 0 },
    { label: 'Cancelled', value: Number(cards.totalCancelledJobs) || 0 },
    { label: 'CAD', value: Number(cards.totalCadJobs) || 0 },
    { label: 'Remaining', value: Number(cards.totalRemainingJobs) || 0 }
  ];
}

export function driverDigestStatItems(summary: any, isRange: boolean): any[] {
  return [
    { label: 'Checked In', value: Number(summary.checkedIn) || 0 },
    { label: 'Total Sessions', value: Number(summary.totalSessions) || 0 },
    { label: 'No Timesheet', value: Number(summary.noTimesheet) || 0 },
    { label: 'Off / Idle', value: Number(summary.offIdle) || 0 },
    {
      label: isRange ? 'Total Hours' : 'Avg Hours Today',
      value: Number(isRange ? summary.totalHours : summary.avgHoursToday) || 0
    },
    {
      label: isRange ? 'Avg Hours / Session' : 'Avg Week Hours',
      value: Number(isRange ? summary.avgHoursPerSession : summary.avgWeekHours) || 0
    },
    { label: 'Check-In Rate (%)', value: Number(summary.checkInRate) || 0 }
  ];
}

export function timelinessStatItems(kpiCards: any): any[] {
  return [
    { label: 'On-Time Rate (%)', value: Number(kpiCards.onTime) || 0 },
    { label: 'Early Rate (%)', value: Number(kpiCards.early) || 0 },
    { label: 'Late Rate (%)', value: Number(kpiCards.late) || 0 },
    { label: 'Completed Trips', value: Number(kpiCards.total) || 0 }
  ];
}

export function mapOverviewFundingItems(rows: any[]): any[] {
  return (rows || []).map((row) => ({
    label: cooperateDisplayName(row),
    value: row.totalJobs != null ? row.totalJobs : 0
  }));
}

export function mapOverviewBreakdownItems(rows: any[], nameField: string): any[] {
  return (rows || []).map((row) => ({
    label: breakdownRowName(row, nameField),
    value: row.totalJobs != null ? row.totalJobs : 0
  }));
}

export function timelinessPercentSegments(timeliness: any = {}): any[] {
  return [
    { label: 'On Time', value: timeliness.onTime != null ? timeliness.onTime : 0, color: CHART_COLORS[1] },
    { label: 'Early', value: timeliness.early != null ? timeliness.early : 0, color: CHART_COLORS[3] },
    { label: 'Late', value: timeliness.late != null ? timeliness.late : 0, color: CHART_COLORS[2] }
  ].filter((item) => item.value > 0);
}
